// src/utils/userGovernance.js
//
// District tenancy Phase 3f: who may create users, tier guardrails, and pending
// approval. Pure logic — no Firebase. See Developer/DISTRICT_TENANCY_DESIGN.md §5b.

/** @typedef {'district_only'|'school_admins'|'school_admins_and_sped_chairs'} WhoCanCreate */

export const DEFAULT_USER_GOVERNANCE = {
  whoCanCreateUsers: 'school_admins_and_sped_chairs',
  requireDistrictApproval: false
}

/** Higher = more privilege. Used only for assignable-role UI guardrails. */
export const ROLE_TIER = {
  admin: 100,
  district_admin: 95,
  district_coordinator: 93,
  school_admin: 90,
  admin_504: 90,
  sped_chair: 85,
  sped_chair_all: 85,
  staff_edit: 50,
  staff_edit_docs: 50,
  staff_view: 40,
  case_manager: 40,
  case_manager_full: 40,
  speech_case_manager: 40,
  teacher: 30,
  teacher_full: 30,
  service_provider: 30,
  school_psych: 30,
  paraeducator: 20
}

/** Roles only a district_admin or admin may assign. */
export const DISTRICT_ONLY_ROLES = ['admin', 'district_admin', 'district_coordinator']

/** School-admin tier — sped_chair creators may not assign these. */
export const SCHOOL_ADMIN_TIER_ROLES = ['school_admin', 'admin_504']

const CREATOR_ROLES_BY_MODE = {
  district_only: ['admin', 'district_admin', 'district_coordinator'],
  school_admins: ['admin', 'district_admin', 'district_coordinator', 'school_admin', 'admin_504'],
  school_admins_and_sped_chairs: [
    'admin',
    'district_admin',
    'district_coordinator',
    'school_admin',
    'admin_504',
    'sped_chair',
    'sped_chair_all'
  ]
}

export function normalizeUserGovernance(raw = {}) {
  const who = raw.whoCanCreateUsers || DEFAULT_USER_GOVERNANCE.whoCanCreateUsers
  const whoCanCreateUsers = CREATOR_ROLES_BY_MODE[who]
    ? who
    : DEFAULT_USER_GOVERNANCE.whoCanCreateUsers
  return {
    whoCanCreateUsers,
    requireDistrictApproval: !!raw.requireDistrictApproval
  }
}

export function roleTier(role) {
  return ROLE_TIER[role] ?? 0
}

export function canCreateUsers(actorRole, governance = DEFAULT_USER_GOVERNANCE) {
  // Matches firestore.rules canCreateUsersByPolicy — admin/district_admin always.
  if (actorRole === 'admin' || actorRole === 'district_admin') return true
  const g = normalizeUserGovernance(governance)
  const allowed = CREATOR_ROLES_BY_MODE[g.whoCanCreateUsers] || []
  return allowed.includes(actorRole)
}

export function canApproveUsers(actorRole) {
  return actorRole === 'admin' || actorRole === 'district_admin'
}

/**
 * Who may change a user's school scope (schoolIds / primarySchoolId) in the
 * Admin user editor. Mirrors firestore.rules validAdminUserUpdate — district
 * tier only, because a school-level admin rewriting schoolIds is a
 * cross-school PII grant.
 *
 * Narrow exception: Cloud Function `addCurrentSchoolToUser` lets a creator
 * append *their* school only (SEIS / staff import link). It never removes
 * schools or changes primarySchoolId / role.
 */
export function canEditUserSchools(actorRole) {
  return actorRole === 'admin' || actorRole === 'district_admin'
}

export function canManageGovernanceSettings(actorRole) {
  return actorRole === 'admin' || actorRole === 'district_admin'
}

/**
 * Whether `creatorRole` may assign `targetRole` (tier + district-only rules).
 * @param {string} creatorRole
 * @param {string} targetRole
 */
export function canAssignRole(creatorRole, targetRole) {
  if (!creatorRole || !targetRole) return false
  if (DISTRICT_ONLY_ROLES.includes(targetRole)) {
    return creatorRole === 'admin' || creatorRole === 'district_admin'
  }
  if (SCHOOL_ADMIN_TIER_ROLES.includes(targetRole)) {
    if (creatorRole === 'sped_chair' || creatorRole === 'sped_chair_all') return false
    if (creatorRole === 'district_coordinator') return false
  }
  return roleTier(targetRole) <= roleTier(creatorRole)
}

/**
 * Filter a role list to those the creator may assign.
 * @param {string} creatorRole
 * @param {string[]} allRoles
 */
export function assignableRoles(creatorRole, allRoles = []) {
  return allRoles.filter((r) => canAssignRole(creatorRole, r))
}

/**
 * Resolve status + schoolIds for a new user doc.
 * @param {object} opts
 * @param {string} opts.creatorRole
 * @param {string[]} opts.creatorSchoolIds
 * @param {object} [opts.governance]
 * @param {string} opts.targetRole
 * @param {string[]|undefined} opts.requestedSchoolIds
 * @returns {{ ok: true, status: 'active'|'pending', schoolIds: string[] } | { ok: false, error: string }}
 */
export function buildNewUserGovernanceFields({
  creatorRole,
  creatorSchoolIds = [],
  governance = DEFAULT_USER_GOVERNANCE,
  targetRole,
  requestedSchoolIds
}) {
  const g = normalizeUserGovernance(governance)

  if (!canCreateUsers(creatorRole, g)) {
    return { ok: false, error: 'Your role is not permitted to create users.' }
  }
  if (!canAssignRole(creatorRole, targetRole)) {
    return { ok: false, error: `You cannot assign the role "${targetRole}".` }
  }

  const isDistrictCreator =
    creatorRole === 'admin' ||
    creatorRole === 'district_admin' ||
    creatorRole === 'district_coordinator'

  let schoolIds = []
  if (isDistrictCreator) {
    if (Array.isArray(requestedSchoolIds) && requestedSchoolIds.length) {
      schoolIds = [...requestedSchoolIds]
    } else if (creatorSchoolIds.length) {
      schoolIds = [creatorSchoolIds[0]]
    }
  } else {
    if (creatorSchoolIds.length !== 1) {
      return {
        ok: false,
        error: 'School-level user creation requires exactly one school in your scope.'
      }
    }
    schoolIds = [creatorSchoolIds[0]]
    if (
      Array.isArray(requestedSchoolIds) &&
      requestedSchoolIds.length &&
      !requestedSchoolIds.every((id) => id === creatorSchoolIds[0])
    ) {
      return { ok: false, error: 'You may only create users for your own school.' }
    }
  }

  const needsApproval = g.requireDistrictApproval && !canApproveUsers(creatorRole)
  const status = needsApproval ? 'pending' : 'active'

  return { ok: true, status, schoolIds }
}
